// utils/weatherUtils.js
import React from "react";
import WbSunnyIcon from "@mui/icons-material/WbSunny"; // Full Moon, Clear day
import Brightness2Icon from "@mui/icons-material/Brightness2"; // New Moon / Waxing/Waning Crescent, Clear night
import Brightness4Icon from "@mui/icons-material/Brightness4"; // Half Moon / Quarter Moon
import Brightness5Icon from "@mui/icons-material/Brightness5"; // Waxing/Waning Gibbous
import CloudIcon from "@mui/icons-material/Cloud"; // Clouds
import GrainIcon from "@mui/icons-material/Grain"; // Drizzle
import UmbrellaIcon from "@mui/icons-material/Umbrella"; // Rain
import AcUnitIcon from "@mui/icons-material/AcUnit"; // Snow
import ThunderstormIcon from "@mui/icons-material/Thunderstorm"; // Thunderstorm
import hazeIcon from "@mui/icons-material/Dehaze"; // Mist, Smoke, Haze, Fog, Sand, Dust, Ash
import TornadoIcon from "@mui/icons-material/Tornado"; // Tornado

/**
 * Maps a numerical moon phase to a descriptive string.
 * Phase 0 and 1 are New Moon and Full Moon respectively.
 * Other values are fractions between 0 and 1.
 * @param {number} phase - The moon phase (0-1).
 * @returns {string} The descriptive moon phase name.
 */
export const getMoonPhase = (phase) => {
  if (phase === 0 || phase === 1) return "New Moon";
  if (phase > 0 && phase < 0.25) return "Waxing Crescent";
  if (phase === 0.25) return "First Quarter";
  if (phase > 0.25 && phase < 0.5) return "Waxing Gibbous";
  if (phase === 0.5) return "Full Moon";
  if (phase > 0.5 && phase < 0.75) return "Waning Gibbous";
  if (phase === 0.75) return "Last Quarter";
  if (phase > 0.75 && phase < 1) return "Waning Crescent";
  return "Unknown Phase";
};

/**
 * Maps a numerical moon phase to an appropriate Material-UI icon.
 * @param {number} phase - The moon phase (0-1).
 * @returns {React.ElementType} The Material-UI icon component.
 */
export const getMoonPhaseIcon = (phase) => {
  // Using Material-UI icons for visual representation
  if (phase === 0) return <Brightness2Icon />; // New Moon
  if (phase > 0 && phase < 0.25)
    return <Brightness2Icon sx={{ transform: "scaleX(-1)" }} />; // Waxing Crescent (half-moon icon, flipped)
  if (phase === 0.25) return <Brightness4Icon />; // First Quarter
  if (phase > 0.25 && phase < 0.5) return <Brightness5Icon />; // Waxing Gibbous
  if (phase === 0.5) return <WbSunnyIcon />; // Full Moon (using sun icon for brightness)
  if (phase > 0.5 && phase < 0.75)
    return <Brightness5Icon sx={{ transform: "scaleX(-1)" }} />; // Waning Gibbous (half-moon icon, flipped)
  if (phase === 0.75)
    return <Brightness4Icon sx={{ transform: "scaleX(-1)" }} />; // Last Quarter
  if (phase > 0.75 && phase < 1) return <Brightness2Icon />; // Waning Crescent
  return <Brightness2Icon />; // Default to a general moon icon
};

/**
 * Formats a Unix timestamp into a readable time string (e.g., "03:30 PM").
 * @param {number} timestamp - The Unix timestamp (in seconds).
 * @param {boolean} includeSeconds - Whether to include seconds in the format.
 * @returns {string} The formatted time string.
 */
export const formatTime = (timestamp, includeSeconds = false) => {
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: includeSeconds ? "2-digit" : undefined,
  });
};

/**
 * Formats a Date object into a readable date string (e.g., "Jun 12").
 * @param {Date} date - The Date object.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date) => {
  if (!(date instanceof Date)) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * Formats a Date object into a full date and time string (e.g., "June 12, 2025, 03:30 PM").
 * @param {Date} date - The Date object.
 * @param {boolean} includeSeconds - Whether to include seconds in the time part.
 * @returns {string} The formatted full date and time string.
 */
export const formatFullDateTime = (date, includeSeconds = false) => {
  if (!(date instanceof Date)) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: includeSeconds ? "2-digit" : undefined,
  });
};

/**
 * Array of full day names for mapping.
 */
export const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Array of short day names for mapping.
 */
export const shortDaysOfWeek = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

/**
 * Returns a Material-UI icon component based on OpenWeatherMap weather condition code.
 * @param {number} conditionCode - The OpenWeatherMap weather condition code.
 * @param {string} iconId - The OpenWeatherMap icon ID (e.g., "01d", "01n") to differentiate day/night.
 * @returns {React.ElementType} The Material-UI icon component.
 */
export const getWeatherIcon = (conditionCode, iconId) => {
  const isDay = iconId.endsWith("d");

  // Group 2xx: Thunderstorm
  if (conditionCode >= 200 && conditionCode <= 232) {
    return <ThunderstormIcon />;
  }
  // Group 3xx: Drizzle
  if (conditionCode >= 300 && conditionCode <= 321) {
    return <GrainIcon />;
  }
  // Group 5xx: Rain
  if (conditionCode >= 500 && conditionCode <= 531) {
    return <UmbrellaIcon />;
  }
  // Group 6xx: Snow
  if (conditionCode >= 600 && conditionCode <= 622) {
    return <AcUnitIcon />;
  }
  // Group 7xx: Atmosphere (Mist, Smoke, Haze, Dust, Fog, Sand, Ash, Squall, Tornado)
  if (conditionCode >= 701 && conditionCode <= 771) {
    return <hazeIcon />; // Using Dehaze icon for general atmospheric conditions
  }
  if (conditionCode === 781) {
    return <TornadoIcon />; // Tornado specific
  }
  // Group 800: Clear
  if (conditionCode === 800) {
    return isDay ? <WbSunnyIcon /> : <Brightness2Icon />; // Sun for day, moon for night
  }
  // Group 80x: Clouds
  if (conditionCode >= 801 && conditionCode <= 804) {
    return <CloudIcon />;
  }

  // Default icon if no match
  return <CloudIcon />;
};

/**
 * Provides a human-readable description based on OpenWeatherMap weather condition code.
 * @param {number} conditionCode - The OpenWeatherMap weather condition code.
 * @returns {string} The weather description.
 */
export const getWeatherDescription = (conditionCode) => {
  switch (conditionCode) {
    case 200:
    case 201:
    case 202:
    case 210:
    case 211:
    case 212:
    case 221:
    case 230:
    case 231:
    case 232:
      return "Thunderstorm";
    case 300:
    case 301:
    case 302:
    case 310:
    case 311:
    case 312:
    case 313:
    case 314:
    case 321:
      return "Drizzle";
    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
    case 511:
    case 520:
    case 521:
    case 522:
    case 531:
      return "Rain";
    case 600:
    case 601:
    case 602:
    case 611:
    case 612:
    case 613:
    case 615:
    case 616:
    case 620:
    case 621:
    case 622:
      return "Snow";
    case 701:
      return "Mist";
    case 711:
      return "Smoke";
    case 721:
      return "Haze";
    case 731:
      return "Sand/Dust Whirls";
    case 741:
      return "Fog";
    case 751:
      return "Sand";
    case 761:
      return "Dust";
    case 762:
      return "Ash";
    case 771:
      return "Squalls";
    case 781:
      return "Tornado";
    case 800:
      return "Clear Sky";
    case 801:
      return "Few Clouds";
    case 802:
      return "Scattered Clouds";
    case 803:
      return "Broken Clouds";
    case 804:
      return "Overcast Clouds";
    default:
      return "Unknown";
  }
};
